import { Autocomplete, Box, Grid, TextField, TextareaAutosize } from '@mui/material'
import { ConsortiumRole, LegalEntityParty, TriContractPartyGroupRoleType } from '@types'
import { useContext, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { RootState, useDispatch, useSelector } from 'redux/store'
import { getAllLegalEntitiesSuccess, clearAllLegalEntites } from 'redux/slices/legalEntities'
import { clearAllTermsConditions } from 'redux/slices/terms'
import { GlobalInfoContext } from 'contexts/GlobalStateContext'
import { useSocketConnection } from 'hooks/useSocketConnection'
import { generateID } from 'utils/generateMessageID'
import { REQUEST_MESSAGE_TYPES, Request } from '../../../schemas/request'
import Item from 'pages/dashboard/sub-sections/items'
import { PointOfContact } from './forms/types/PointOfContact'
import BusinessIdAutoComplete from './BusinessIdentity'
import PointOfContactEditor from './PointOfContactEditor'
import PointOfContactSummary from './PointOfContactSummary'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

export type LegalEntityCodeType = 'ABN' | undefined

export const legalEntityBaseValue = {
  commonName: '',
  onboarded: true,
  contactFirstName: '',
  contactLastName: '',
  email: '',
  phone: '',
  legalEntityIdentifier: '',
  legalEntityCodes: [
    {
      legalEntityCodeType: 'ABN' as LegalEntityCodeType,
      legalEntityCodeData: ''
    }
  ]
}

type EntityProps = {
  label: string
  type: string
  touched?: any
  errors?: any
  getFieldProps?: any
  setValue: (value: LegalEntityParty) => void
  optionalSelectedValue?: LegalEntityParty
  initialOption?: LegalEntityParty
  handleClear: (value) => void
  initiatedBy: string
  initialValues: LegalEntityParty[] | any
  resetTermsRef?: React.RefObject<any>
  resetIssuer?: React.RefObject<any>
  issuerRef?: React.RefObject<any>
  clearable?: boolean
  singleEntry?: boolean
  setPOC?: (value?: PointOfContact | undefined) => void
  pocDataFromParent?: PointOfContact | any
  isOnbordedRequired?: boolean
}

export const EntityDetailsRow = forwardRef(
  (
    {
      label,
      type,
      touched,
      errors,
      getFieldProps,
      setValue,
      initialOption,
      handleClear,
      initiatedBy,
      initialValues,
      resetTermsRef,
      resetIssuer,
      issuerRef,
      setPOC,
      pocDataFromParent,
      singleEntry,
      isOnbordedRequired
    }: EntityProps,
    ref
  ) => {
    const dispatch = useDispatch()
    const { entities } = useSelector((state: RootState) => state?.legalEntities)
    const { issuers } = useSelector((state: RootState) => state?.termsReducer)
    const { onSocketSend } = useSocketConnection()
    const [orgs, setOrgsList] = useState<LegalEntityParty[]>([])
    const [labelEntered, setLabelEntered] = useState<string>('')
    const [optionSelected, setOption] = useState<LegalEntityParty>(
      initialOption || legalEntityBaseValue
    )
    const [isSelected, setIsSelected] = useState(false)
    const { applicants, beneficiaries, id, guaranteeType } = useContext(GlobalInfoContext)
    const designatedPOC = beneficiaries?.[0]?.designatedPointOfContact
    const [open, setOpen] = useState(false)

    const [showPocForm, setShowPocForm] = useState(false)
    const [poc, setPoc] = useState<PointOfContact | null>(null)
    const pocFirstNameRef = useRef<HTMLInputElement | null>(null)

    useImperativeHandle(ref, () => ({
      resetOptions() {
        setOption(legalEntityBaseValue)
        setOpen(false)
      }
    }))

    useImperativeHandle(issuerRef, () => ({
      resetIssuerOptions() {
        if (type === 'issuers') {
          setOption(legalEntityBaseValue)
          setOpen(false)
        }
      }
    }))

    useEffect(() => {
      if (!Array.isArray(initialValues) || initialValues.length !== 1) return
      let leEntity: LegalEntityParty | null = null
      if (type === 'issuers') {
        leEntity = initialValues[0]?.issuerLegalEntity || null
      } else if (type === 'applicants') {
        leEntity = initialValues[0]?.businessUnitActionContext?.[0]?.legalEntity || null
      }
      if (
        leEntity &&
        leEntity.legalEntityIdentifier &&
        leEntity.legalEntityIdentifier !== optionSelected?.legalEntityIdentifier
      ) {
        setOption(leEntity)
        setValue(leEntity)
      }
    }, [type, initialValues, optionSelected?.legalEntityIdentifier])

    useEffect(() => {
      if (type === 'applicants') {
        const applicantEntity = initialValues?.[0]?.businessUnitActionContext?.[0]?.legalEntity
        if (applicantEntity) {
          setOrgsList([applicantEntity])
        }
      } else if (type === 'issuers') {
        const issuerEntity = initialValues?.map((o) => o.issuerLegalEntity)
        if (issuerEntity) {
          setOrgsList([...issuerEntity])
        }
      }
    }, [initialValues, type])

    useEffect(() => {
      if (type !== 'issuers' && guaranteeType === 'digital') {
        const requestTermsAndConditions: Request = {
          messageId: generateID(),
          messageType: REQUEST_MESSAGE_TYPES.QueryAvailableContractTerms,
          QueryAvailableContractTerms: {
            legalEntityIdentifier:
              initiatedBy === 'applicant'
                ? applicants?.[0]?.legalEntityIdentifier
                : beneficiaries?.[0]?.legalEntityIdentifier,
            partyGroupRole:
              initiatedBy === 'applicant'
                ? TriContractPartyGroupRoleType.APPLICANT
                : TriContractPartyGroupRoleType.BENE,
            purposeTypeIdentifier: id,
            applicants: [
              {
                legalEntityIdentifier: applicants?.[0]?.legalEntityIdentifier,
                ConsortiumRole: ConsortiumRole.SOLE_LE,
                onboarded: true
              }
            ],
            beneficiaries: [
              {
                legalEntityIdentifier: beneficiaries?.[0]?.legalEntityIdentifier,
                ConsortiumRole: ConsortiumRole.SOLE_LE,
                onboarded: true
              }
            ]
          }
        }
        onSocketSend(requestTermsAndConditions)
      }
    }, [isSelected, guaranteeType])

    const handleShowPointOfContact = (show: boolean) => {
      if (type === 'beneficiaries') {
        setShowPocForm(show)
      }
    }

    const handlePocDisplayCheck = (entity: LegalEntityParty) => {
      const abn = entity?.legalEntityCodes?.find(
        (c) => c.legalEntityCodeType === 'ABN' || c.legalEntityCodeType === 'ACN'
      )?.legalEntityCodeData
      const isABNValid = abn?.length === 11
      const isOnboarded = entity?.onboarded
      if (type === 'beneficiaries' && isABNValid && isOnboarded) {
        setShowPocForm(true)
      }
    }

    useEffect(() => {
      const contextPOC = beneficiaries?.[0]?.designatedPointOfContact
      if (type === 'beneficiaries' && contextPOC?.person) {
        const { person, position } = contextPOC
        setPoc({ person, position })
      }
    }, [beneficiaries, type])

    useEffect(() => {
      if (type === 'beneficiaries' && showPocForm && pocFirstNameRef.current) {
        setTimeout(() => {
          pocFirstNameRef.current?.focus()
        }, 50)
      }
    }, [showPocForm])

    return (
      <>
        <Grid item xs={4}>
          <Item>
            <Box>
              {(type === 'applicants' && orgs.length === 1) ||
              (type === 'issuers' && orgs.length === 1) ? (
                <FauxTextField
                  label={label}
                  value={
                    type === 'applicants'
                      ? initialValues?.[0]?.businessUnitActionContext?.[0]?.legalEntity
                          ?.commonName ?? ''
                      : orgs?.[0]?.commonName ?? ''
                  }
                  singleEntry
                  showSuccess
                />
              ) : type !== 'beneficiaries' ? (
                <Autocomplete
                  disablePortal
                  id={`${type}`}
                  options={orgs}
                  sx={{ minWidth: 180 }}
                  isOptionEqualToValue={(option, value) =>
                    option?.legalEntityIdentifier === value?.legalEntityIdentifier
                  }
                  getOptionLabel={(options: LegalEntityParty) => options?.commonName || ''}
                  onInputChange={(event, value, reason) => {
                    if (reason === 'clear') {
                      setOption(legalEntityBaseValue)
                      setLabelEntered('')
                      handleClear(type)
                      resetTermsRef?.current?.resetTerms()
                      resetIssuer?.current?.resetIssuerOptions()
                      dispatch(clearAllLegalEntites())
                      dispatch(clearAllTermsConditions())
                      setIsSelected(false)
                    } else {
                      setLabelEntered(value)
                    }
                  }}
                  onChange={(event, option, reason) => {
                    if (reason === 'selectOption' && option) {
                      setOption(option)
                      setValue(option)
                      setIsSelected(true)
                      handlePocDisplayCheck(option)
                    }
                  }}
                  value={optionSelected}
                  onBlur={() => {
                    setOpen(false)
                    dispatch(clearAllLegalEntites())
                  }}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={label}
                      sx={{ width: '98%' }}
                      placeholder="Please select..."
                      autoComplete="off"
                      InputLabelProps={{ shrink: true }}
                      {...getFieldProps?.(`${type}`)}
                      error={Boolean(touched?.[`${type}`] && errors?.[`${type}`])}
                      helperText={touched?.[`${type}`] && errors?.[`${type}`]}
                    />
                  )}
                />
              ) : optionSelected?.commonName ? (
                <FauxTextField
                  label={`${label}`}
                  value={optionSelected?.commonName}
                  singleEntry
                  showSuccess
                />
              ) : null}
            </Box>
          </Item>
        </Grid>

        <Grid item xs={1} />

        <Grid item xs={4}>
          <Item>
            <BusinessIdAutoComplete
              optionSelected={optionSelected}
              setLabelEntered={setLabelEntered}
              setOption={setOption}
              setValue={(val) => {
                setOption(val)
                setValue(val)
                handlePocDisplayCheck(val)
              }}
              label={label.split(' ')[0]}
              type={type}
              initialValues={initialValues}
              isSelected={isSelected}
              clearable={false}
              resetIssuer={resetIssuer}
              resetTermsRef={resetTermsRef}
              handleClear={handleClear}
              setShowPointOfContact={handleShowPointOfContact}
              setPOC={setPoc}
              singleEntry={singleEntry}
              pocFirstNameRef={pocFirstNameRef}
            />
          </Item>
        </Grid>

        {type === 'beneficiaries' && !optionSelected?.onboarded && (
          <>
            {showPocForm ? (
              <PointOfContactEditor
                data={poc || designatedPOC || undefined}
                refObj={{ firstName: pocFirstNameRef }}
                required={!isOnbordedRequired}
                onSubmit={(data) => {
                  setPoc(data)
                  setPOC?.(data)
                  setShowPocForm(false)
                }}
                onClear={() => {
                  setPoc(null)
                  setPOC?.(undefined)
                  setShowPocForm(true)
                }}
              />
            ) : (
              <PointOfContactSummary
                data={poc || designatedPOC}
                onEdit={() => {
                  setShowPocForm(true)
                }}
                onClear={() => {
                  setPoc(null)
                  setPOC?.(undefined)
                  setShowPocForm(true)
                }}
              />
            )}
          </>
        )}
      </>
    )
  }
)

export const FauxTextField = ({
  label,
  value,
  singleEntry,
  showSuccess = false
}: {
  label: string
  value: string
  singleEntry?: boolean
  showSuccess?: boolean
}) => (
  <Box
    sx={{
      width: '98%',
      border: '1px solid rgba(0, 0, 0, 0.23)',
      borderRadius: '8px',
      padding: '0 14px',
      height: '56px',
      display: 'flex',
      alignItems: 'center',
      fontSize: '16px',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      lineHeight: '1.4375em',
      color: '#000',
      backgroundColor: '#f0f4f8',
      position: 'relative',
      justifyContent: 'space-between',
      marginTop: '1px'
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: '-8px',
        left: '12px',
        fontSize: '12px',
        padding: '0 4px',
        backgroundColor: '#f0f4f8',
        color: 'rgba(0, 0, 0, 0.6)',
        lineHeight: 1
      }}
    >
      {label}
    </Box>
    <Box sx={{ flexGrow: 1 }}>{value}</Box>
    {singleEntry && (
      <Box sx={{ marginLeft: '8px', marginRight: '4px' }}>
        <LockOutlinedIcon sx={{ color: '#9e9e9e' }} />
      </Box>
    )}
    {showSuccess && (
      <Box sx={{ marginLeft: '8px', marginRight: '4px' }}>
        <CheckCircleIcon color="success" />
      </Box>
    )}
  </Box>
)
